import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bottles, Catalog, Occasions, type Bottle, type TastingNote } from '@/lib/api';
import { Card, Icon, Skeleton, Pill, Badge, EmptyState, Button, type IconName } from '@/design-system';
import { BottleCard, BottleCardSkeleton, BottleImage } from '@/components/BottleCard';
import {
  FOOD_CATEGORY_LABELS,
  TASTE_LABELS,
  WINE_TYPE_LABELS,
  shoppingQueryFor,
  type OccasionCriteria,
} from '@/lib/occasion-presets';
import { buildSmakProfile } from '@/lib/smak';
import { formatPriceNok } from '@/lib/format';

const MOOD_ICON: Record<string, IconName> = {
  bbq: 'flame',
  christmas: 'snow',
  tuesday: 'moon',
  anniversary: 'sparkles',
  party: 'party',
  fish: 'fish',
  game: 'beef',
  cheese: 'cookie',
  asian: 'cherry',
  default: 'utensils',
};

export function OccasionDetailPage() {
  const { id = '' } = useParams();
  const occasionsQ = useQuery({ queryKey: ['occasions'], queryFn: () => Occasions.list() });
  const matchesQ = useQuery({
    queryKey: ['occasion.matches', id],
    queryFn: () => Occasions.matches(id),
    enabled: Boolean(id),
  });
  const cellarQ = useQuery({
    queryKey: ['cellar.bottles.smak'],
    queryFn: () => Bottles.list({ limit: 200 }),
  });

  const occasion = occasionsQ.data?.find((o) => o.id === id);
  const criteria = (occasion?.criteria as Partial<OccasionCriteria>) ?? {};

  // Build smak profile from cellar + notes
  const smakNotesQ = useQuery({
    queryKey: ['smak.notes', cellarQ.data?.bottles.length ?? 0],
    queryFn: async () => {
      const bottles = cellarQ.data?.bottles ?? [];
      const notesArrays = await Promise.all(
        bottles.map((b) => Bottles.notes(b.id).catch(() => [] as TastingNote[])),
      );
      const map = new Map<string, TastingNote[]>();
      bottles.forEach((b, i) => map.set(b.id, notesArrays[i] ?? []));
      return map;
    },
    enabled: (cellarQ.data?.bottles.length ?? 0) > 0,
  });

  const smak = useMemo(() => {
    if (!cellarQ.data?.bottles || !smakNotesQ.data) return null;
    return buildSmakProfile(cellarQ.data.bottles, smakNotesQ.data);
  }, [cellarQ.data, smakNotesQ.data]);

  // Shopping list — catalog suggestions matching wine type / budget.
  const shoppingQ = useQuery({
    queryKey: ['shopping', id, criteria.wineTypes?.[0], criteria.budgetPerBottleNok],
    queryFn: () => Catalog.search(shoppingQueryFor(criteria as OccasionCriteria), 12),
    enabled: Boolean(occasion),
  });

  const guests = criteria.guestCount ?? 4;
  const bpg = criteria.bottlesPerGuest ?? 0.5;
  const bottlesNeeded = Math.ceil(guests * bpg);
  const matches = matchesQ.data ?? [];
  const haveCount = matches.reduce((sum, b) => sum + b.qty, 0);
  const missingCount = Math.max(0, bottlesNeeded - haveCount);

  if (occasionsQ.isLoading) {
    return (
      <div className="grid gap-4">
        <Skeleton height={28} width="240px" />
        <Skeleton height={120} />
      </div>
    );
  }
  if (!occasion) {
    return <Card className="p-6">Anledningen ble ikke funnet.</Card>;
  }

  const moodKey = (occasion.mood ?? 'default').toLowerCase();
  const icon = MOOD_ICON[moodKey] ?? MOOD_ICON.default ?? 'utensils';

  return (
    <div className="grid gap-8">
      <div>
        <Link to="/occasions" className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle">
          <Icon name="chevron-left" size={14} /> Tilbake
        </Link>
      </div>

      {/* Header */}
      <Card className="grid gap-4 p-6">
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-candle/15 text-candle">
            <Icon name={icon} size={24} />
          </div>
          <div className="grid flex-1">
            <h1 className="font-serif text-3xl text-parchment">{occasion.name}</h1>
            {occasion.description && <p className="text-sm text-mist">{occasion.description}</p>}
          </div>
          <div className="grid place-items-end gap-1 text-right">
            <Badge tone={missingCount > 0 ? 'ruby' : 'success'}>
              {haveCount} / {bottlesNeeded} flasker
            </Badge>
            <span className="text-[10px] uppercase tracking-widest text-mist">{guests} gjester · {bpg}/p</span>
          </div>
        </div>

        {/* Criteria summary */}
        {(criteria.wineTypes?.length || criteria.tastes?.length || criteria.foodCategories?.length) && (
          <div className="flex flex-wrap gap-1.5">
            {criteria.wineTypes?.map((w) => (
              <Pill key={w} variant="gold">{WINE_TYPE_LABELS[w]}</Pill>
            ))}
            {criteria.foodCategories?.map((f) => (
              <Pill key={f} icon={FOOD_CATEGORY_LABELS[f].icon}>{FOOD_CATEGORY_LABELS[f].label}</Pill>
            ))}
            {criteria.tastes?.map((t) => (
              <Pill key={t}>{TASTE_LABELS[t]}</Pill>
            ))}
            {criteria.budgetPerBottleNok && (
              <Pill icon="tag">≤ {formatPriceNok(criteria.budgetPerBottleNok)}</Pill>
            )}
          </div>
        )}
      </Card>

      {/* From cellar */}
      <section className="grid gap-3">
        <div className="flex items-center gap-3">
          <Icon name="rack" className="text-candle" size={18} />
          <h2 className="font-serif text-2xl text-parchment">Fra kjelleren</h2>
          <Badge tone={haveCount > 0 ? 'gold' : 'mist'} className="ml-2">
            {haveCount} klare
          </Badge>
        </div>
        {matchesQ.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <BottleCardSkeleton key={i} />)}
          </div>
        ) : matches.length === 0 ? (
          <Card className="p-6 text-sm text-mist">
            Ingen flasker fra kjelleren matcher denne anledningen — se katalogforslag under.
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {matches.map((b: Bottle) => (
              <BottleCard key={b.id} bottle={b} />
            ))}
          </div>
        )}
      </section>

      {/* Smak — taste profile */}
      <section className="grid gap-3">
        <div className="flex items-center gap-3">
          <Icon name="sparkles" className="text-candle" size={18} />
          <h2 className="font-serif text-2xl text-parchment">Din smak</h2>
        </div>
        {smakNotesQ.isLoading || cellarQ.isLoading ? (
          <Skeleton height={120} />
        ) : !smak || smak.highlights.length === 0 ? (
          <Card className="p-6 text-sm text-mist">
            Vi bygger smaks-profil fra dine vurderinger på 4★+. Vurder noen flasker så blir dette mer presist.
          </Card>
        ) : (
          <Card className="grid gap-4 p-5 lg:grid-cols-[1fr_1.2fr]">
            <div className="grid gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mist">Foretrekker</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {smak.topTastes.map((t) => (
                    <Pill key={t} variant="gold">{TASTE_LABELS[t]}</Pill>
                  ))}
                  {smak.topTastes.length === 0 && <span className="text-xs text-mist">For lite data enda</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mist">Topp områder</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {smak.topRegions.map((r) => <Badge key={r} tone="gold">{r}</Badge>)}
                  {smak.topCountries.map((r) => <Badge key={r} tone="ruby">{r}</Badge>)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mist">Topp druer</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {smak.topGrapes.map((g) => <Badge key={g} tone="mist">{g}</Badge>)}
                </div>
              </div>
              <div className="text-xs text-mist">
                Snittvurdering på flasker du har vurdert: <span className="font-semibold text-parchment">{smak.averageRating.toFixed(1)} / 5</span>
              </div>
            </div>
            <div className="grid gap-2">
              <div className="text-[10px] uppercase tracking-widest text-mist">Hjørnesteiner i kjelleren</div>
              {smak.highlights.map(({ bottle, rating, comment }) => (
                <Link
                  key={bottle.id}
                  to={`/bottle/${bottle.id}`}
                  className="ui-card ui-card--interactive grid grid-cols-[auto_1fr_auto] items-center gap-3 p-2"
                >
                  <div className="relative grid h-10 w-8 place-items-center overflow-hidden rounded bg-ink-soft">
                    <BottleImage src={bottle.photoUrl} iconSize={14} />
                  </div>
                  <div className="grid">
                    <span className="text-sm text-parchment line-clamp-1">{bottle.name}</span>
                    {comment && <span className="text-[11px] text-mist line-clamp-1">{comment}</span>}
                  </div>
                  <Badge tone="gold"><Icon name="star" size={10} /> {rating.toFixed(1)}</Badge>
                </Link>
              ))}
            </div>
          </Card>
        )}
      </section>

      {/* Shopping list — catalog */}
      <section className="grid gap-3">
        <div className="flex items-center gap-3">
          <Icon name="add-bottle" className="text-candle" size={18} />
          <h2 className="font-serif text-2xl text-parchment">Handleliste</h2>
          <Badge tone={missingCount > 0 ? 'ruby' : 'success'} className="ml-2">
            mangler {missingCount}
          </Badge>
        </div>
        {missingCount === 0 ? (
          <Card className="p-6">
            <EmptyState icon="check-circle" title="Du har nok i kjelleren" description="Ingenting å handle for denne anledningen." />
          </Card>
        ) : shoppingQ.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <BottleCardSkeleton key={i} />)}
          </div>
        ) : (
          <ShoppingResults
            criteria={criteria as OccasionCriteria}
            need={missingCount}
            data={shoppingQ.data?.products ?? []}
            preferredCountries={smak?.topCountries ?? []}
            preferredRegions={smak?.topRegions ?? []}
          />
        )}
      </section>
    </div>
  );
}

function ShoppingResults({
  criteria,
  need,
  data,
  preferredCountries,
  preferredRegions,
}: {
  criteria: OccasionCriteria;
  need: number;
  data: import('@/lib/api').VinmonopoletProduct[];
  preferredCountries: string[];
  preferredRegions: string[];
}) {
  // Filter by budget if present
  const max = criteria.budgetPerBottleNok;
  const ranked = data
    .map((p) => {
      const price = (p.prices as { salesPrice?: number } | undefined)?.salesPrice;
      const country = (p.origins as { country?: string } | undefined)?.country ?? '';
      const region = (p.origins as { region?: string } | undefined)?.region ?? '';
      const budgetOk = !max || (price != null && price <= max);
      const fitsTaste =
        (preferredCountries.includes(country) ? 1 : 0) + (preferredRegions.includes(region) ? 1.5 : 0);
      return { p, price, country, region, budgetOk, score: fitsTaste };
    })
    .filter((x) => x.budgetOk)
    .sort((a, b) => b.score - a.score);

  if (ranked.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState icon="bottle-empty" title="Ingen katalogtreff" description="Juster smak/budsjett og prøv igjen." />
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {ranked.slice(0, Math.max(8, need * 3)).map(({ p, price, score }) => (
        <div key={p.basic.productId} className="grid gap-2">
          <BottleCard product={p} highlight={score > 1} />
          <div className="flex items-center justify-between text-xs text-mist">
            <span>{formatPriceNok(price)}</span>
            <a
              href={`https://www.vinmonopolet.no/p/${p.basic.productId}`}
              target="_blank"
              rel="noreferrer"
              className="hover:text-candle"
            >
              Åpne kilde <Icon name="external" size={10} className="inline" />
            </a>
          </div>
        </div>
      ))}
      <div className="ui-card grid place-items-center gap-2 p-6 text-center sm:col-span-2">
        <Icon name="info" className="text-mist" size={20} />
        <p className="text-sm text-parchment">Forslagene er rangert etter ditt smaks-profil og budsjett.</p>
        <Link to="/scan"><Button variant="ghost" iconLeft="scan">Skann en flaske du fant</Button></Link>
      </div>
    </div>
  );
}
